// Campaigns and their posted videos, from the same endpoints the Campaign
// page uses (the main API, team.influencer.in):
//   campaigns: GET /api/v1/brand/campaign/optimized   (api_handlers/campaign)
//   videos:    GET /api/v1/campaign/:id/social_media/feeds/{youtube|instagram}
//              (api_handlers/invoice, normalised by invoices/lib/campaignPosts)
import { getCampaigns } from 'api_handlers/campaign';
import { getCampaignFeed } from 'api_handlers/invoice';
import { POST_PLATFORMS, hasMorePages, normalisePost } from 'components/invoices/lib/campaignPosts';

// The feed returns ten rows a page; stop after 200 posts per platform.
export const MAX_FEED_PAGES = 20;

const PLATFORM_LABEL = { youtube: 'YouTube', instagram: 'Instagram' };

export const mapCampaign = (c) => ({
    id: c.id,
    name: c.name || 'Untitled campaign',
    brand: c.brand_name || '',
    status: c.is_live ? 'active' : String(c.status || 'draft').toLowerCase(),
    startDate: c.start_date || null,
    endDate: c.end_date || null,
    objective: c.objective || c.description || '',
});

export async function listCampaigns() {
    const results = await getCampaigns();
    return (Array.isArray(results) ? results : []).filter((c) => c && c.id != null).map(mapCampaign);
}

// A feed post as a row the QC table can check. The post url is what QC
// downloads; the platform-prefixed id ties the QC result back to the post.
export const postToVideo = (post) => ({
    id: post.id,
    title: post.title || `${post.label} by ${post.userName || 'creator'}`,
    source: post.url,
    creatorName: post.userName,
    publishedAt: post.at,
    platform: PLATFORM_LABEL[post.platform] || post.platform,
});

// YouTube rows name the creator in channel_title, Instagram rows in user_name.
const withCreator = (post, row) => post && { ...post, userName: post.userName || String(row.channel_title || '').trim() };

async function postsFor(campaignId, platform) {
    let posts = [];
    let fetched = 0;
    for (let page = 1; page <= MAX_FEED_PAGES; page += 1) {
        // eslint-disable-next-line no-await-in-loop
        const body = await getCampaignFeed(campaignId, platform, page);
        const rows = Array.isArray(body?.results) ? body.results : [];
        fetched += rows.length;
        posts = [...posts, ...rows.map((row) => withCreator(normalisePost(row, platform), row)).filter(Boolean)];
        if (!hasMorePages(body, fetched)) break;
    }
    return posts;
}

// Both platforms, de-duplicated by url. One platform failing still shows the
// other; both failing is an error.
export async function listCampaignVideos(campaignId) {
    const settled = await Promise.allSettled(POST_PLATFORMS.map((platform) => postsFor(campaignId, platform)));
    if (settled.every((s) => s.status === 'rejected')) throw settled[0].reason;
    const posts = settled.flatMap((s) => (s.status === 'fulfilled' ? s.value : []));
    const seen = new Set();
    const unique = posts.filter((p) => (seen.has(p.url) ? false : seen.add(p.url)));
    return {
        videos: unique.map(postToVideo),
        partial: settled.some((s) => s.status === 'rejected'),
    };
}
